/**
 * Fold away the reply chain a message carried with it.
 *
 * Every mail client answers by quoting the whole conversation underneath the
 * two sentences somebody actually wrote. The thread pane rendered all of it,
 * so a ten-message thread printed the first message ten times over and the
 * newest text - the only part that is new - sat on top of a wall the operator
 * had to scroll past to reach the next bubble. This finds where the new text
 * ends and the chain begins.
 *
 * String work rather than DOM work, for two reasons. It runs under node:test
 * without a DOM, and it only ever sees the sanitiser's own output: DOMPurify
 * serialises through the DOM, so tags come back lowercase and attributes come
 * back double quoted, which is what lets the patterns below stay this simple.
 * The order is therefore not negotiable - sanitise first, split second.
 * Splitting first would hand the sanitiser two fragments and let a marker
 * sitting inside an attacker's attribute decide where we cut.
 *
 * Recognised: Gmail, Outlook (desktop and web), Thunderbird, Yahoo and Apple
 * Mail in HTML; `>` prefixes, "... wrote:" / "... kirjutas ...:" attribution
 * lines and the Original Message dividers in plain text. Not recognised: a
 * client that quotes with none of those. Then nothing folds and the message
 * reads exactly as it did before, which is the point - the failure mode here
 * is the old behaviour, never a sentence the operator cannot see.
 */

export type QuoteSplit = {
  /** What the sender wrote this time. */
  main: string;
  /** The chain below it, or null when there is nothing worth folding. */
  quoted: string | null;
};

/** Block-level tags a client can hang its quote marker on. */
const HTML_TAG_RE = /<(?:div|blockquote|table|hr|p)\b[^>]*>/gi;

/** Tested against one opening tag, not against the whole document. */
const HTML_MARKERS: RegExp[] = [
  /class="[^"]*\bgmail_quote(?:_container)?\b/i,
  /class="[^"]*\bgmail_extra\b/i,
  /id="divRplyFwdMsg"/i,
  /id="appendonsend"/i,
  /class="[^"]*\bmoz-cite-prefix\b/i,
  /(?:class|id)="[^"]*\byahoo_quoted\b/i,
  // A bare <blockquote> is somebody quoting a sentence on purpose; `type="cite"`
  // is a client quoting a message. Only the second one is a chain.
  /^<blockquote\b[^>]*\btype="cite"/i,
];

/** The divider some clients write into the body text instead of a wrapper. */
const HTML_DIVIDER_RE =
  /-{3,}\s*(?:original message|forwarded message|algne sõnum|edastatud sõnum)\s*-{3,}/i;

const TEXT_DIVIDER_RE =
  /^\s*(?:-{2,}\s*(?:original message|forwarded message|algne sõnum|edastatud sõnum)\s*-{2,}|_{10,})\s*$/i;

const QUOTED_LINE_RE = /^\s{0,3}>/;

/** "On <date>, X <x@y.ee> wrote:" and its Estonian twin, both one line. */
const ATTRIBUTION_RE = /\b(?:wrote|kirjutas)\b[^\n]*:\s*$/i;

/**
 * Is there anything left above the cut?
 *
 * A forward is what makes this a rule and not a nicety: it is almost entirely
 * quoted chain, so cutting at the marker can leave an empty bubble with a "..."
 * button under it. An unreadable message is worse than a long one.
 */
function hasVisibleContent(html: string): boolean {
  if (/<img\b/i.test(html)) return true;
  return html.replace(/<[^>]*>/g, " ").replace(/&nbsp;/gi, " ").trim().length > 0;
}

export function splitQuotedHtml(html: string): QuoteSplit {
  if (!html) return { main: html, quoted: null };

  let cut = -1;
  for (const m of html.matchAll(HTML_TAG_RE)) {
    if (HTML_MARKERS.some((re) => re.test(m[0]))) {
      cut = m.index;
      break;
    }
  }

  // A text divider counts too, but only when it comes first: a wrapper tells us
  // where the element starts, a run of dashes only tells us where the words do.
  const divider = html.search(HTML_DIVIDER_RE);
  if (divider >= 0 && (cut < 0 || divider < cut)) {
    const open = html.lastIndexOf("<", divider);
    cut = open >= 0 ? open : divider;
  }

  if (cut <= 0) return { main: html, quoted: null };
  const main = html.slice(0, cut);
  if (!hasVisibleContent(main)) return { main: html, quoted: null };
  return { main, quoted: html.slice(cut) };
}

/**
 * `requireAddress` is the difference between a line that quotes and a line that
 * merely says "wrote". Right above a `>` block the quote is already proven, so
 * the line only has to look like an attribution. Standing on its own it has to
 * carry a sender address as well, or "everything he wrote is below:" would fold
 * away the rest of somebody's message.
 */
function isAttribution(line: string, requireAddress: boolean): boolean {
  if (line.length > 300) return false;
  if (!ATTRIBUTION_RE.test(line)) return false;
  return requireAddress ? line.includes("@") : true;
}

/** The attribution belongs to the quote, not to the reply above it. */
function attributionAbove(lines: string[], start: number): number {
  for (let k = start - 1; k >= 0 && start - k <= 3; k--) {
    const line = lines[k] ?? "";
    if (!line.trim()) continue;
    return isAttribution(line, false) ? k : start;
  }
  return start;
}

export function splitQuotedText(text: string): QuoteSplit {
  if (!text) return { main: text, quoted: null };

  const lines = text.split(/\r?\n/);
  let cut = -1;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i] ?? "";
    if (TEXT_DIVIDER_RE.test(line)) {
      cut = i;
      break;
    }
    if (QUOTED_LINE_RE.test(line)) {
      cut = attributionAbove(lines, i);
      break;
    }
    if (isAttribution(line, true)) {
      cut = i;
      break;
    }
  }

  if (cut < 0) return { main: text, quoted: null };
  const main = lines.slice(0, cut).join("\n").replace(/\s+$/, "");
  if (!main.trim()) return { main: text, quoted: null };
  return { main, quoted: lines.slice(cut).join("\n").replace(/\s+$/, "") };
}
